import { optionKeyOf, type FilterOption } from './productFilterTypes';

type Slot =
  | { kind: 'single'; option: FilterOption }
  | { kind: 'group'; key: string; members: FilterOption[] };

/**
 * Collapses the options of one manually listed category into the rows the
 * reader ticks (CMS-2087).
 *
 * Options sharing a non-empty `groupKey` become one row: keyed by the group
 * key as it arrives, sitting where its first member sits, labelled with the
 * `groupLabel` its members carry. The row's `members` are what it matches, and
 * its `aliases` are the members' own keys, so a URL written before grouping
 * still selects it. An option with no group key is returned untouched.
 *
 * An ungrouped option whose own key equals a group's key is folded into that
 * group as a further member, after the ones that named it. Otherwise two rows
 * would share one URL token and the reader could never tell which a link meant.
 */
export const groupFilterOptions = (
  options: readonly FilterOption[],
): FilterOption[] => {
  const slots: Slot[] = [];
  const groups = new Map<string, Extract<Slot, { kind: 'group' }>>();

  options.forEach((option) => {
    const key = option.groupKey;
    if (!key) {
      slots.push({ kind: 'single', option });
      return;
    }
    const existing = groups.get(key);
    if (existing) {
      existing.members.push(option);
      return;
    }
    const slot: Extract<Slot, { kind: 'group' }> = {
      kind: 'group',
      key,
      members: [option],
    };
    groups.set(key, slot);
    slots.push(slot);
  });

  if (groups.size === 0) return [...options];

  const folded = new Map<string, FilterOption[]>();
  const kept = slots.filter((slot) => {
    if (slot.kind !== 'single') return true;
    const key = optionKeyOf(slot.option);
    if (!groups.has(key)) return true;
    folded.set(key, [...(folded.get(key) ?? []), slot.option]);
    return false;
  });

  return kept.map((slot): FilterOption => {
    if (slot.kind === 'single') return slot.option;

    const members = [...slot.members, ...(folded.get(slot.key) ?? [])];
    return {
      value: slot.key,
      key: slot.key,
      label: slot.members[0].groupLabel,
      members,
      aliases: members.map(optionKeyOf),
    };
  });
};
